// Based on https://getbootstrap.com/docs/5.1/components/modal/

<template>
    <div id="confirmModal" class="modal fade" tabindex="-1" aria-labelledby="confirmModalLabel" aria-hidden="true">
        <div :class="`modal-dialog modal-dialog-scrollable modal-dialog-centered ${largeModal ? 'modal-lg' : ''}`">
            <div class="modal-content">
                <div v-if="title" class="modal-header">
                    <h5 id="confirmModalLabel" class="modal-title">{{ title }}</h5>
                    <button-button type="button" class="btn-close" aria-label="Close" @click="cancelClicked"></button-button>
                </div>
                <div class="modal-body">
                    {{ message }}
                </div>
                <div class="modal-footer">
                    <button-button v-if="cancelButton" type="button" class="btn btn-secondary" @click="cancelClicked">{{ cancelButton }}</button-button>
                    <button-button v-if="alt1Button" type="button" class="btn btn-secondary" @click="alt1Clicked">{{ alt1Button }}</button-button>
                    <button-button v-if="okButton" type="button" class="btn btn-primary" @click="okClicked">{{ okButton }}</button-button>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss">
    #confirmModal {
        z-index: 2000; // place above real modal
    }
</style>

<script lang="ts">

import { defineComponent, markRaw } from "vue";
import { Modal } from "bootstrap";
import { ButtonClicked } from "@/shared/enums/ButtonClicked";
import OverlaySuppression from "@/shared/support/OverlaySuppression";

export interface AlertDialogOptions {
    okButton?: string;
    alt1Button?: string;
    cancelButton?: string;
    largeModal?: boolean;
}

let cachedAlertDialog: any = null;

export default defineComponent({
    name: "AlertDialog",
    data() {
        return {
            title: null as string | null,
            message: null as unknown as string,
            okButton: null as string | null,
            alt1Button: null as string | null,
            cancelButton: null as string | null,
            largeModal: false,
            // One Bootstrap instance for the life of the component. Two instances on the one shared
            // element fight each other: the outgoing one's hide sets display:none over whatever
            // replaced it, and its hidden event carries no instance reference, so it cannot be told
            // apart from the replacement's own.
            modalInstance: null as Modal | null,
            isShown: false,
            isHiding: false,
            pendingShow: false,
            // Null once the current dialog has been settled, which is also what makes a second
            // button click a no-op.
            resolvePromise: null as ((value: ButtonClicked | PromiseLike<ButtonClicked>) => void) | null
        };
    },
    methods: {
        internalShow(message: string, title?: string, options?: AlertDialogOptions): Promise<ButtonClicked> {
            // Takes over from whatever is on screen rather than hiding it first, so the content swaps
            // in place and a replaced dialog can never blank the one replacing it.
            this.dropPending();
            this.title = title ?? null;
            this.message = message || "";
            this.okButton = options?.okButton || null;
            this.alt1Button = options?.alt1Button || null;
            this.cancelButton = options?.cancelButton || null;
            this.largeModal = options?.largeModal || false;
            OverlaySuppression.acquire(this);
            const promise = new Promise<ButtonClicked>((resolve): void => {
                this.resolvePromise = resolve;
            });
            if (this.isHiding)
                this.pendingShow = true;// show() is ignored mid-transition, so wait for hidden
            else
                this.showModal();

            return promise;
        },
        // The backdrop is adjusted here rather than in internalShow so that a dialog deferred until
        // the previous one has finished hiding gets the same treatment as one shown straight away.
        showModal(): void {
            this.modalInstance?.show();

            // to support multiple modals we need to adjust the backdrop zindex
            const backdrops = document.querySelectorAll(".modal-backdrop.show");
            if (backdrops.length > 1) {
                const backdrop = backdrops[backdrops.length - 1] as HTMLElement;
                backdrop.style.zIndex = "1999";
            }
        },
        okClicked(): void {
            this.settleWith(ButtonClicked.Ok);
        },
        cancelClicked(): void {
            this.settleWith(ButtonClicked.Cancel);
        },
        alt1Clicked(): void {
            this.settleWith(ButtonClicked.Alt1);
        },
        settleWith(button: ButtonClicked): void {
            const resolve = this.resolvePromise;
            if (!resolve) return;
            this.resolvePromise = null;
            this.hideModal();
            resolve(button);
        },
        killAlertDialog(): void {
            this.dropPending();
            this.hideModal();
        },
        hideModal(): void {
            this.pendingShow = false;
            // Only a dialog that has finished appearing actually transitions out; hide() is ignored
            // while one is still ramping up, and claiming otherwise would strand pendingShow.
            this.isHiding = this.isShown;
            this.modalInstance?.hide();
        },
        // Closed without the operator choosing anything. Callers treat anything that is not Ok as a
        // decline, so the promise is left unsettled rather than resolved as one.
        dropPending(): void {
            this.resolvePromise = null;
        },
        // True from the moment a dialog is raised until it has left the screen, so a caller deferring
        // to an operator's own prompt is not let through during the closing transition.
        isOpen(): boolean {
            return this.isShown || this.pendingShow || this.resolvePromise != null;
        }
    },
    mounted(): void {
        const element = this.$el as HTMLElement;
        this.modalInstance = markRaw(Modal.getOrCreateInstance(element, {
            backdrop: "static",
            keyboard: true,
            focus: true,
        }));
        element.addEventListener("shown.bs.modal", (): void => {
            this.isShown = true;
            // Killed while it was still ramping up. Bootstrap ignores hide() mid-transition, so the
            // dialog would otherwise stay on screen with nothing left to settle.
            if (!this.resolvePromise) this.hideModal();
        });
        // Esc and any programmatic hide bypass the button handlers, so the pending dialog is cleared here
        // as well, or isOpen() goes on reporting a dialog that has left the screen. The screen is released
        // only now, once the dialog has fully left it.
        element.addEventListener("hidden.bs.modal", (): void => {
            this.isShown = false;
            this.isHiding = false;
            if (this.pendingShow) {
                this.pendingShow = false;
                this.showModal();
                return;
            }
            this.dropPending();
            OverlaySuppression.release(this);
        });
    }
});

export function initializeAlertDialog(dialog: any): void {
    cachedAlertDialog = dialog;
}

export function killAlertDialogs(): void {
    cachedAlertDialog.killAlertDialog();
}

export function isAlertDialogOpen(): boolean {
    return cachedAlertDialog?.isOpen() === true;
}

export function showAlertDialog(message: string, title?: string, options?: AlertDialogOptions): Promise<ButtonClicked> {
    return cachedAlertDialog.internalShow(message, title, options);
}

export function confirmYesNo(message: string, title?: string): Promise<ButtonClicked> {
    return showAlertDialog(message, title, { okButton: "Yes", cancelButton: "No" });
}

export function confirmOk(message: string, title?: string): Promise<ButtonClicked> {
    return showAlertDialog(message, title, { okButton: "OK" });
}

export function confirmYesWaitNo(message: string, title?: string): Promise<ButtonClicked> {
    return showAlertDialog(message, title, { okButton: "Yes", alt1Button: "Wait", cancelButton: "No" });
}

</script>
